/**
 * Turning what a user pastes into a coordinate.
 *
 * This exists because of a measured gap, not a preference. The product's place search asks a geocoder
 * by *name*, and the recall ceiling of the free stack is real: `Phong Miên quán - forest slow café`
 * did not resolve to a usable coordinate for us, while the user had it open in Google Maps. Ten real
 * Đà Lạt venues measured earlier: seven resolve, two are absent from OSM entirely, one resolves to the
 * city. The user is already looking at the right place — the cheapest way to get its coordinate is to
 * let them hand it over rather than to make the geocoder find what it does not have.
 *
 * **Pure, and that is why it lives here rather than in the providers package.** Everything below is
 * string work with no network: the web app parses a full Google Maps URL or a bare coordinate pair in
 * the browser and adopts the pin with no request at all, and only a *short* link needs the server to
 * follow its redirect. Both sides read the same parser, so the two cannot disagree about what a URL
 * means.
 *
 * The one decision worth stating outright is which coordinate out of a Google Maps URL to believe.
 * Measured on the reported link (`maps.app.goo.gl/GxW5aP4oZLDYraVeA`) the two candidates in one URL
 * are **280 m apart**:
 *
 * | Where | Value | What it is |
 * |---|---|---|
 * | `data=!3d…!4d…` | 11.9213822, 108.4428672 | the place's own pin |
 * | `@…,17z` | 11.9213874, 108.4402923 | where the *camera* was, offset so the pin is not hidden behind Google's side panel |
 *
 * `PIN` wins, and the camera is only the fallback for URLs that carry no pin (`/maps/@lat,lng,zoom`).
 * 280 m is inside the same ward but on a different street, and this project's own rule is that a
 * plausible wrong coordinate is worse than a missing one — so the pin, and the user still sees it as a
 * draggable marker before saving.
 */

/** A coordinate pair, in the order every consumer of this file expects: latitude then longitude. */
export interface GeoCoord {
  lat: number;
  lon: number;
}

/** What a pasted string turned out to be. */
export type LocationInput =
  | { kind: "coordinates"; location: GeoCoord }
  /** A full Google Maps URL: the coordinate is in the string, so no request is needed. */
  | { kind: "maps-url"; location: GeoCoord; name: string | null; url: string }
  /** A `maps.app.goo.gl` / `goo.gl/maps` link: the coordinate is behind a redirect only the server can follow. */
  | { kind: "maps-short-link"; url: string }
  /** A Google Maps URL we recognise but that carries no coordinate — a name search, for instance. */
  | { kind: "maps-url-without-coordinate"; url: string }
  /** Everything else: a place name, which is the geocoder's job. */
  | { kind: "text" };

/** Latitude and longitude both present and inside their ranges, or null. */
function validPair(lat: number, lon: number): GeoCoord | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

/**
 * A number as a human writes it.
 *
 * The comma case is not hypothetical tidy-up: this product ships in Vietnamese, where the decimal
 * separator *is* a comma. Measured, `11,9213822 108,4428672` — the form Google Maps shows in a
 * `vi-VN` locale — parses as NaN unless the comma is converted, and a NaN coordinate is exactly the
 * silent bad value this codebase refuses everywhere else. A comma is read as a decimal point only when
 * the group holds no dot already; `11,940.5` keeps its thousands separator meaning nothing here because
 * a coordinate never has one, so the second comma form is simply not a number.
 */
function numberOf(raw: string): number {
  const trimmed = raw.trim();
  const normalised = trimmed.includes(".") ? trimmed.replaceAll(",", "") : trimmed.replace(",", ".");
  return Number(normalised);
}

/**
 * A bare coordinate pair, which is what Google Maps gives a user who long-presses a pin.
 *
 * A full-string match, deliberately. A substring match would read the `11` and `108` out of
 * `Quán ăn 11, 108 Nguyễn Chí Thanh` and pin the place in the Gulf of Thailand — and a wrong
 * coordinate is invisible, which is the failure this project spends the most effort avoiding.
 *
 * Accepts the decorations people actually copy: `11.92, 108.44`, `11.92 108.44`, `11,92 108,44`,
 * `11.92° N, 108.44° E`, with optional hemisphere letters either side.
 */
export function parseCoordinateText(text: string): GeoCoord | null {
  /*
   * The group order is latitude, its hemisphere letter, longitude, its hemisphere letter — and the
   * indices below are read off that, not off the order the numbers appear in the string. Pointing at
   * groups 1 and 2 (latitude and the *letter* `N`) is what the first version did, and it threw on every
   * pair without a hemisphere letter: `match[2]` is `undefined` there, and `Number(undefined)` is NaN but
   * `.trim()` on it is a crash. The tests caught it on six inputs at once.
   */
  const match =
    /^\s*(-?\d{1,3}(?:[.,]\d+)?)\s*(?:°)?\s*([NS])?\s*[,;\s]\s*(-?\d{1,3}(?:[.,]\d+)?)\s*(?:°)?\s*([EW])?\s*$/i.exec(
      text,
    );
  if (match === null) return null;
  const latRaw = numberOf(match[1] as string);
  const lonRaw = numberOf(match[3] as string);
  // A southern or western hemisphere letter flips the sign, which is how those are written.
  const lat = (match[2] ?? "").toUpperCase() === "S" ? -Math.abs(latRaw) : latRaw;
  const lon = (match[4] ?? "").toUpperCase() === "W" ? -Math.abs(lonRaw) : lonRaw;
  return validPair(lat, lon);
}

/** Whether this text is a URL at all, using the same rule the capture form uses for its leading field. */
function asUrl(text: string): URL | null {
  const trimmed = text.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  try {
    return new URL(trimmed);
  } catch {
    return null;
  }
}

/** Every host a Google Maps share link arrives on. `maps.app.goo.gl` is what the mobile share sheet gives. */
const SHORT_LINK_HOSTS = ["maps.app.goo.gl", "goo.gl"];

/**
 * A Google domain, anchored so a look-alike cannot pass.
 *
 * **This is the check that decides which coordinates may be trusted, so it is written to match a
 * registrable domain rather than a prefix.** The previous version used
 * `/^(?:www\.)?google\.[a-z.]{2,}$/`, and `[a-z.]{2,}` matches *any* dotted suffix — so measured,
 * `https://google.evil.com/maps/@1.5,2.5,3z` was accepted, its coordinate was adopted as a Google
 * coordinate, and the URL was stored as the place's source. That is precisely the invariant this module
 * claims to hold ("a link may only move the pin to a coordinate Google itself published") and it did not
 * hold. A domain someone can register must not be able to pin a user's place.
 *
 * The shape is `google.<tld>` or `google.<sld>.<tld>`, where the tld and the optional second level are
 * short alphabetic labels: `google.com`, `google.com.vn`, `google.co.uk`, `google.com.au`. `google.evil.com`
 * fails because the label after `google.` is four characters, which no Google ccTLD second level is.
 */
const GOOGLE_APEX = /^google\.[a-z]{2,3}(?:\.[a-z]{2})?$/;

/** Whether a parsed URL is a Google Maps page. */
function isGoogleMapsUrl0(url: URL): boolean {
  const host = url.hostname.toLowerCase();
  // The share-sheet host and the general shortener, whose maps path is the only one of interest.
  if (host === "maps.app.goo.gl") return true;
  if (host === "goo.gl") return url.pathname.startsWith("/maps");

  // `maps.google.com/…` — the old form, where the subdomain carries the meaning and the path is often `/`.
  if (host.startsWith("maps.") && GOOGLE_APEX.test(host.slice("maps.".length))) return true;

  // `google.com/maps/…`, `www.google.com/maps/…`, `google.com.vn/maps/…` — a Maps page is the `/maps` path.
  const apex = host.startsWith("www.") ? host.slice("www.".length) : host;
  if (GOOGLE_APEX.test(apex)) return url.pathname.startsWith("/maps");

  return false;
}

/**
 * Whether this is a Google Maps link whose coordinate only a redirect can reveal.
 *
 * **https on the default port, and nothing else.** The hostname is not the whole URL, and the parts this
 * ignores are the parts that decide what the server actually does: measured,
 * `https://maps.app.goo.gl:8443/GxW5aP4oZLDYraVeA` made the API open a TCP connection to an arbitrary port
 * and hold it for 21 s — twice, since the call retries — while `serviceGate` serialises every request for
 * this provider, so one such paste queues every other user's resolution behind it. An `http://` share
 * link is also a real input (it is what a desktop browser may show), and requesting it costs an extra
 * round trip because Google 301s to the https form; the caller upgrades the scheme instead.
 */
export function isGoogleMapsShortLink(text: string): boolean {
  const url = asUrl(text);
  if (url === null || url.port !== "") return false;
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  /*
   * Credentials are refused rather than stripped.
   *
   * A share link never legitimately carries them, and `fetch` throws on a URL that does — a throw that
   * `withProviderCall` classifies as a network failure and retries, so a crafted link would burn three
   * attempts on something that can never succeed. Refusing here means it never becomes a request.
   */
  if (url.username !== "" || url.password !== "") return false;
  return isGoogleMapsUrl0(url) && SHORT_LINK_HOSTS.includes(url.hostname.toLowerCase());
}

/*
 * There is one predicate for "a short link we are willing to request", used for the pasted URL *and* for a
 * resolved target.
 *
 * There were two, and they differed: the second omitted the credentials check, so
 * `https://u:p@maps.app.goo.gl/…` passed the resolver's own validation and reached `fetch`, which throws on
 * a URL carrying credentials — a throw classified as a network failure and retried, on something that can
 * never succeed. Two predicates with different bars for one question is the defect; the fix is not to add
 * the missing check to the second, it is to have one.
 */



/** Whether this is a Google Maps URL of any shape. */
export function isGoogleMapsUrl(text: string): boolean {
  const url = asUrl(text);
  return url !== null && isGoogleMapsUrl0(url);
}

/**
 * The coordinate inside a full Google Maps URL, and the place's name when the URL carries one.
 *
 * Only for URLs that already hold a coordinate. A short link holds none — that is the whole point of
 * it — and returns null here, which is what sends the caller to the server.
 */
export function parseGoogleMapsUrl(text: string): { location: GeoCoord; name: string | null } | null {
  const url = asUrl(text);
  if (url === null || !isGoogleMapsUrl0(url)) return null;
  const full = url.toString();
  // The place pin first, then the camera, then an explicit query parameter. Each is a different fact and
  // the order is the one documented at the top of this file.
  const pin = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/.exec(full);
  if (pin !== null) {
    const location = validPair(Number(pin[1]), Number(pin[2]));
    if (location !== null) return { location, name: placeNameFrom(url) };
  }
  const camera = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(full);
  if (camera !== null) {
    const location = validPair(Number(camera[1]), Number(camera[2]));
    if (location !== null) return { location, name: placeNameFrom(url) };
  }
  /*
   * Query parameters, and deliberately only the ones that name a *destination*.
   *
   * `ll` and `center` were in this list and were removed: they are the **view centre**, not the place.
   * Measured, `https://www.google.com/maps?ll=11.9,108.4&q=Cho+Da+Lat` returned `11.9, 108.4` as the
   * place's coordinate — a search link that carries a map centre, reported as `ok`. That is the plausible
   * wrong coordinate this project treats as worse than none, and it also defeated the `no_coordinate`
   * state the contract reserves for exactly this link. The camera is still a fallback, but only from
   * `@lat,lng,zoom`, where it is unambiguously a view of the place path. `q`/`query`, `destination` and
   * `daddr` name where the user is going, which is the question being asked.
   */
  for (const key of ["q", "query", "destination", "daddr"]) {
    const raw = url.searchParams.get(key);
    if (raw === null) continue;
    const location = parseCoordinateText(raw);
    if (location !== null) return { location, name: placeNameFrom(url) };
  }
  return null;
}

/** The `/maps/place/<name>/` segment, decoded. `+` is a space in that position, not a literal plus. */
function placeNameFrom(url: URL): string | null {
  const match = /\/maps\/place\/([^/@?]+)/.exec(url.pathname);
  if (match === null) return null;
  try {
    const decoded = decodeURIComponent((match[1] as string).replaceAll("+", " ")).trim();
    return decoded === "" ? null : decoded;
  } catch {
    // A malformed percent-escape. A missing name is a field the user can fill; a thrown exception here
    // would lose the coordinate the URL did carry.
    return null;
  }
}

/**
 * What this pasted string is, decided once and in one place.
 *
 * The order matters: a coordinate pair is checked before a URL (a bare pair is not a URL), and a short
 * link before a full URL (a short link is a URL that carries no coordinate, so it must not fall through
 * to the parser that would report "no coordinate here").
 */
export function classifyLocationInput(text: string): LocationInput {
  const trimmed = text.trim();
  if (trimmed === "") return { kind: "text" };

  if (isGoogleMapsShortLink(trimmed)) {
    return { kind: "maps-short-link", url: trimmed };
  }
  if (isGoogleMapsUrl(trimmed)) {
    const parsed = parseGoogleMapsUrl(trimmed);
    return parsed === null ? { kind: "maps-url-without-coordinate", url: trimmed } : { kind: "maps-url", ...parsed, url: trimmed };
  }
  const coordinates = parseCoordinateText(trimmed);
  if (coordinates !== null) return { kind: "coordinates", location: coordinates };
  return { kind: "text" };
}
